import React from 'react';
import PropTypes from 'prop-types';
import RadioGroup from './RadioGroup';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Components/Inputs/RadioGroup',
  component: RadioGroup,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `RadioGroup` component provides a group of radio buttons for selecting one option from a list. It ensures consistency and accessibility in forms.\n\n',
      // When & how to use it?
      '\nUse the `RadioGroup` component whenever you need to present a list of mutually exclusive options to the user.\n\n',
      // Example usage
      '\nThe `RadioGroup` component is used in export season modal and filters to provide a consistent user experience for selecting options.\n\n',
      // Related Components
      `\n- **RadioButton**: The individual radio button component used within the \`RadioGroup\`.
      \n- **Multiselect**: A similar component for multiple selections.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    options: {
      control: { type: 'object' },
      description: 'Array of options for the radio group. Each option should have `id`, `name`, and `checked` properties.',
    },
    radioGroupLabel: {
      control: { type: 'text' },
      description: 'Label for the radio group.',
    },
    setValue: {
      action: 'setValue',
      description: 'Callback function to handle the selected value.',
    },
    sectionClassName: {
      control: { type: 'text' },
      description: 'Additional class names for the section containing the radio buttons.',
    },
    radioGroupLabelFont: {
      control: { type: 'text' },
      description: 'Font classes for the radio group label.',
    },
    radioGroupLabelClasses: {
      control: { type: 'text' },
      description: 'Additional class names for the radio group label.',
    },
    spaceY: {
      control: { type: 'text' },
      description: 'Spacing between radio buttons.',
    },
  },
};

const RadioGroupTemplate = ({
  options,
  radioGroupLabel,
  setValue,
  sectionClassName = '',
  radioGroupLabelFont = 'font-bold',
  radioGroupLabelClasses = 'mb-5',
  spaceY = 'space-y-4',
}) => (
  <RadioGroup
    options={options}
    radioGroupLabel={radioGroupLabel}
    setValue={setValue}
    sectionClassName={sectionClassName}
    radioGroupLabelFont={radioGroupLabelFont}
    radioGroupLabelClasses={radioGroupLabelClasses}
    spaceY={spaceY}
  />
);

const Template = RadioGroupTemplate.bind({});

export const DefaultRadioGroup = Template.bind({});
DefaultRadioGroup.args = {
  options: [
    { id: '1', name: 'Option 1', checked: false },
    { id: '2', name: 'Option 2', checked: false },
    { id: '3', name: 'Option 3', checked: false },
  ],
  radioGroupLabel: 'Select an Option',
};
DefaultRadioGroup.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThe `DefaultRadioGroup` is used as the base style for presenting a group of radio buttons with a label.\n\n',
  // Where is it currently used on the product?
  '\nThis variant is used in export season modal.\n\n',
);

RadioGroupTemplate.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      checked: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  radioGroupLabel: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  sectionClassName: PropTypes.string,
  radioGroupLabelFont: PropTypes.string,
  radioGroupLabelClasses: PropTypes.string,
  spaceY: PropTypes.string,
};
